// Aufbereitung der Kalendereinträge für Monats- und Listenansicht.
//
// Gerechnet wird durchgehend in Ortszeit: die API liefert ganztägige Einträge
// (Wettkämpfe, Saison-Eckdaten) ohne Zeitzonenangabe, Termine mit Uhrzeit als
// UTC. `new Date(...)` ergibt in beiden Fällen den Tag, der gemeint ist.

import { KALENDER_ART } from '../api/apiClient';
import { formatDatum, formatWochentag, formatZeitraum } from './format';

/**
 * Darstellung je Art. `punkt` ist der Farbpunkt der Monatsansicht auf kleinen
 * Bildschirmen, `pille` die Beschriftung darin, `rand` die farbige Kante der
 * Eintragskarte.
 */
export const KALENDER_ARTEN = [
  {
    id: KALENDER_ART.TERMIN,
    label: 'Termine',
    einzahl: 'Termin',
    punkt: 'bg-blue-500',
    pille: 'bg-blue-50 text-blue-800',
    rand: 'border-l-blue-400',
    badge: 'badge-blue',
  },
  {
    id: KALENDER_ART.WETTKAMPF,
    label: 'Wettkämpfe',
    einzahl: 'Wettkampf',
    punkt: 'bg-brand-500',
    pille: 'bg-brand-50 text-brand-800',
    rand: 'border-l-brand-500',
    badge: 'badge-green',
  },
  {
    id: KALENDER_ART.WETTKAMPF_ANSPRECHPARTNER,
    label: 'Wettkämpfe (Ansprechpartner)',
    einzahl: 'Wettkampf (Ansprechpartner)',
    punkt: 'bg-cyan-500',
    pille: 'bg-cyan-50 text-cyan-800',
    rand: 'border-l-cyan-500',
    badge: 'badge-cyan',
  },
  {
    id: KALENDER_ART.WETTKAMPF_HEIM,
    label: 'Heimwettkämpfe',
    einzahl: 'Heim',
    punkt: 'bg-brand-500',
    pille: 'bg-brand-50 text-brand-800',
    rand: 'border-l-brand-500',
    badge: 'badge-green',
  },
  {
    id: KALENDER_ART.WETTKAMPF_AUSWAERTS,
    label: 'Auswärtswettkämpfe',
    einzahl: 'Auswärts',
    punkt: 'bg-red-500',
    pille: 'bg-red-50 text-red-700',
    rand: 'border-l-red-400',
    badge: 'badge-red',
  },
  {
    id: KALENDER_ART.SAISON,
    label: 'Saison',
    einzahl: 'Saison',
    punkt: 'bg-violet-500',
    pille: 'bg-violet-50 text-violet-800',
    rand: 'border-l-violet-400',
    badge: 'badge-gray',
  },
  {
    id: KALENDER_ART.MELDUNG,
    label: 'Meldung',
    einzahl: 'Meldung',
    punkt: 'bg-amber-500',
    pille: 'bg-amber-50 text-amber-800',
    rand: 'border-l-amber-400',
    badge: 'badge-amber',
  },
];

const ART_INFO = new Map(KALENDER_ARTEN.map((a) => [a.id, a]));

/** Darstellung zu einer Art; unbekannte Arten bleiben neutral grau. */
export function artInfo(art) {
  return ART_INFO.get(art) ?? {
    id: art,
    label: art ?? 'Eintrag',
    einzahl: art ?? 'Eintrag',
    punkt: 'bg-slate-400',
    pille: 'bg-slate-100 text-slate-700',
    rand: 'border-l-slate-300',
    badge: 'badge-gray',
  };
}

/** Kennung eines Kalendertages (JJJJ-MM-TT in Ortszeit). */
export function tagesSchluessel(datum) {
  const jahr = datum.getFullYear();
  const monat = String(datum.getMonth() + 1).padStart(2, '0');
  const tag = String(datum.getDate()).padStart(2, '0');
  return `${jahr}-${monat}-${tag}`;
}

export function tagesBeginn(datum) {
  return new Date(datum.getFullYear(), datum.getMonth(), datum.getDate());
}

function tagePlus(datum, anzahl) {
  return new Date(datum.getFullYear(), datum.getMonth(), datum.getDate() + anzahl);
}

/** Erster Tag des Monats, in dem `datum` liegt. */
export function monatsBeginn(datum) {
  return new Date(datum.getFullYear(), datum.getMonth(), 1);
}

export function monatVerschieben(datum, anzahl) {
  return new Date(datum.getFullYear(), datum.getMonth() + anzahl, 1);
}

/**
 * Tage des Monatsrasters: vom Montag der ersten bis zum Sonntag der letzten
 * Woche, damit die Ansicht immer volle Wochenzeilen zeigt.
 */
export function monatsRaster(anker) {
  // getDay(): 0 = Sonntag. Die Woche beginnt hier am Montag, also 0 = Montag.
  const wochentag = (datum) => (datum.getDay() + 6) % 7;

  const erster = monatsBeginn(anker);
  const start = tagePlus(erster, -wochentag(erster));

  const letzter = new Date(anker.getFullYear(), anker.getMonth() + 1, 0);
  const ende = tagePlus(letzter, 6 - wochentag(letzter));

  const tage = [];
  for (let tag = start; tag <= ende; tag = tagePlus(tag, 1)) {
    tage.push(tag);
  }
  return tage;
}

/** Obergrenze gegen fehlerhafte Zeiträume – kein Eintrag deckt ein Jahr ab. */
const MAX_TAGE_JE_EINTRAG = 400;

/**
 * Alle Tage, die ein Eintrag belegt. Ein mehrtägiger Termin erscheint damit an
 * jedem seiner Tage, nicht nur am ersten.
 */
export function belegteTage(eintrag) {
  const beginn = new Date(eintrag.beginn);
  if (Number.isNaN(beginn.getTime())) return [];

  const tage = [tagesSchluessel(beginn)];
  const ende = eintrag.ende ? new Date(eintrag.ende) : null;
  if (!ende || Number.isNaN(ende.getTime())) return tage;

  const letzter = tagesBeginn(ende);
  let tag = tagesBeginn(beginn);
  while (tag < letzter && tage.length < MAX_TAGE_JE_EINTRAG) {
    tag = tagePlus(tag, 1);
    tage.push(tagesSchluessel(tag));
  }
  return tage;
}

/** Einträge je Kalendertag. */
export function nachTagenGruppieren(eintraege) {
  const karte = new Map();
  eintraege.forEach((eintrag) => {
    belegteTage(eintrag).forEach((schluessel) => {
      if (!karte.has(schluessel)) karte.set(schluessel, []);
      karte.get(schluessel).push(eintrag);
    });
  });
  return karte;
}

/**
 * Einträge je Monat, in der Reihenfolge der Einträge. Grundlage der
 * Listenansicht, die nach Monaten überschrieben ist.
 */
export function nachMonatenGruppieren(eintraege) {
  const gruppen = [];
  const nachSchluessel = new Map();

  eintraege.forEach((eintrag) => {
    const beginn = new Date(eintrag.beginn);
    if (Number.isNaN(beginn.getTime())) return;
    const schluessel = `${beginn.getFullYear()}-${String(beginn.getMonth() + 1).padStart(2, '0')}`;
    let gruppe = nachSchluessel.get(schluessel);
    if (!gruppe) {
      gruppe = { schluessel, monat: monatsBeginn(beginn), eintraege: [] };
      nachSchluessel.set(schluessel, gruppe);
      gruppen.push(gruppe);
    }
    gruppe.eintraege.push(eintrag);
  });

  return gruppen;
}

/**
 * Zeitangabe eines Eintrags. Deckt er ganztägig genau einen Tag ab, erscheint
 * nur dieser Tag mit Wochentag: bei einem Wettkampftag oder einem Saisonstart
 * sagt der Zusatz „(ganztägig)“ nichts.
 */
export function zeitangabe(eintrag) {
  const beginn = new Date(eintrag.beginn);
  if (Number.isNaN(beginn.getTime())) return '–';

  const ende = eintrag.ende ? new Date(eintrag.ende) : null;
  const einTag = !ende || Number.isNaN(ende.getTime())
    || tagesSchluessel(beginn) === tagesSchluessel(ende);

  if (eintrag.ganztaegig && einTag) {
    return `${formatWochentag(beginn)}, ${formatDatum(beginn)}`;
  }
  return formatZeitraum(eintrag.beginn, eintrag.ende, eintrag.ganztaegig);
}

/**
 * Ist der Eintrag vorbei? Ein ganztägiger Eintrag ohne Ende gilt bis zum Ende
 * seines Tages – ein Wettkampf ist nicht schon um 00:01 Uhr vergangen.
 */
export function istVergangen(eintrag, jetzt = new Date()) {
  const beginn = new Date(eintrag.beginn);
  if (Number.isNaN(beginn.getTime())) return false;

  if (eintrag.ende) {
    const ende = new Date(eintrag.ende);
    if (!Number.isNaN(ende.getTime())) return ende < jetzt;
  }
  const grenze = eintrag.ganztaegig ? tagePlus(beginn, 1) : beginn;
  return grenze < jetzt;
}

export function istHeute(datum, jetzt = new Date()) {
  return tagesSchluessel(datum) === tagesSchluessel(jetzt);
}
